import {
  odMeanF1,
  prCorrectSureRate,
  prIncorrectSureRate,
  prIncorrectUnsureRate,
  prTop1Accuracy,
  prTop3Accuracy,
  RunResult,
} from './types';

export function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—';
  return (v * 100).toFixed(1) + '%';
}

export function fmtFloat(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toFixed(4);
}

export function fmtCount(v: number | null | undefined): string {
  if (v == null) return '—';
  return String(Math.round(v));
}

export function resultSummary(result: RunResult | null | undefined): string {
  if (!result) return '—';
  const meanF1 = odMeanF1(result.OD);
  if (meanF1 !== null) return `mF1 ${fmtPct(meanF1)}`;
  const prAcc = prTop1Accuracy(result.PR?.products);
  if (prAcc !== null) return `top1 ${fmtPct(prAcc)}`;
  const pogF1 = result.PC?.pog?.compliant?.f1;
  if (pogF1 != null) return `POG F1 ${fmtPct(pogF1)}`;
  return '—';
}

export interface MetricSpec {
  label: string;
  section: 'OD' | 'PR' | 'PC';
  getValue: (result: RunResult) => number | null | undefined;
  /** Custom formatter; defaults to fmtPct */
  fmt?: (v: number | null | undefined) => string;
  /** When true, lower values are better (affects best-value highlighting) */
  lowerIsBetter?: boolean;
  /** Class column group label (e.g. "Products", "Cartons"). Rows sharing the same
   *  consecutive classGroup are merged under a single rowspan cell. */
  classGroup?: string;
  /** Metric label shown in the Metric column when classGroup is set.
   *  Defaults to label when not provided. */
  shortLabel?: string;
}

export const OD_METRICS: MetricSpec[] = [
  { label: 'Mean F1', section: 'OD', getValue: (r) => odMeanF1(r.OD) },
  // Products
  {
    label: 'Products P',
    section: 'OD',
    getValue: (r) => r.OD?.products?.precision,
    classGroup: 'Products',
    shortLabel: 'P',
  },
  {
    label: 'Products R',
    section: 'OD',
    getValue: (r) => r.OD?.products?.recall,
    classGroup: 'Products',
    shortLabel: 'R',
  },
  {
    label: 'Products F1',
    section: 'OD',
    getValue: (r) => r.OD?.products?.f1,
    classGroup: 'Products',
    shortLabel: 'F1',
  },
  {
    label: 'Products N',
    section: 'OD',
    getValue: (r) => r.OD?.products?.total_objects,
    classGroup: 'Products',
    shortLabel: 'N',
    fmt: fmtCount,
  },
  // Cartons
  {
    label: 'Cartons P',
    section: 'OD',
    getValue: (r) => r.OD?.cartons?.precision,
    classGroup: 'Cartons',
    shortLabel: 'P',
  },
  {
    label: 'Cartons R',
    section: 'OD',
    getValue: (r) => r.OD?.cartons?.recall,
    classGroup: 'Cartons',
    shortLabel: 'R',
  },
  {
    label: 'Cartons F1',
    section: 'OD',
    getValue: (r) => r.OD?.cartons?.f1,
    classGroup: 'Cartons',
    shortLabel: 'F1',
  },
  {
    label: 'Cartons N',
    section: 'OD',
    getValue: (r) => r.OD?.cartons?.total_objects,
    classGroup: 'Cartons',
    shortLabel: 'N',
    fmt: fmtCount,
  },
  // Shelves
  {
    label: 'Shelves P',
    section: 'OD',
    getValue: (r) => r.OD?.shelves?.precision,
    classGroup: 'Shelves',
    shortLabel: 'P',
  },
  {
    label: 'Shelves R',
    section: 'OD',
    getValue: (r) => r.OD?.shelves?.recall,
    classGroup: 'Shelves',
    shortLabel: 'R',
  },
  {
    label: 'Shelves F1',
    section: 'OD',
    getValue: (r) => r.OD?.shelves?.f1,
    classGroup: 'Shelves',
    shortLabel: 'F1',
  },
  {
    label: 'Shelves N',
    section: 'OD',
    getValue: (r) => r.OD?.shelves?.total_objects,
    classGroup: 'Shelves',
    shortLabel: 'N',
    fmt: fmtCount,
  },
  // Mods
  {
    label: 'Mods P',
    section: 'OD',
    getValue: (r) => r.OD?.mods?.precision,
    classGroup: 'Mods',
    shortLabel: 'P',
  },
  {
    label: 'Mods R',
    section: 'OD',
    getValue: (r) => r.OD?.mods?.recall,
    classGroup: 'Mods',
    shortLabel: 'R',
  },
  {
    label: 'Mods F1',
    section: 'OD',
    getValue: (r) => r.OD?.mods?.f1,
    classGroup: 'Mods',
    shortLabel: 'F1',
  },
  {
    label: 'Mods N',
    section: 'OD',
    getValue: (r) => r.OD?.mods?.total_objects,
    classGroup: 'Mods',
    shortLabel: 'N',
    fmt: fmtCount,
  },
  // Template Boxes
  {
    label: 'Template Boxes P',
    section: 'OD',
    getValue: (r) => r.OD?.template_boxes?.precision,
    classGroup: 'Template Boxes',
    shortLabel: 'P',
  },
  {
    label: 'Template Boxes R',
    section: 'OD',
    getValue: (r) => r.OD?.template_boxes?.recall,
    classGroup: 'Template Boxes',
    shortLabel: 'R',
  },
  {
    label: 'Template Boxes F1',
    section: 'OD',
    getValue: (r) => r.OD?.template_boxes?.f1,
    classGroup: 'Template Boxes',
    shortLabel: 'F1',
  },
  {
    label: 'Template Boxes N',
    section: 'OD',
    getValue: (r) => r.OD?.template_boxes?.total_objects,
    classGroup: 'Template Boxes',
    shortLabel: 'N',
    fmt: fmtCount,
  },
  // Price Tags
  {
    label: 'Price Tags P',
    section: 'OD',
    getValue: (r) => r.OD?.price_tags?.precision,
    classGroup: 'Price Tags',
    shortLabel: 'P',
  },
  {
    label: 'Price Tags R',
    section: 'OD',
    getValue: (r) => r.OD?.price_tags?.recall,
    classGroup: 'Price Tags',
    shortLabel: 'R',
  },
  {
    label: 'Price Tags F1',
    section: 'OD',
    getValue: (r) => r.OD?.price_tags?.f1,
    classGroup: 'Price Tags',
    shortLabel: 'F1',
  },
  {
    label: 'Price Tags N',
    section: 'OD',
    getValue: (r) => r.OD?.price_tags?.total_objects,
    classGroup: 'Price Tags',
    shortLabel: 'N',
    fmt: fmtCount,
  },
];

export const PR_METRICS: MetricSpec[] = [
  // Products — rates
  {
    label: 'Prod Correct Sure',
    section: 'PR',
    getValue: (r) => prCorrectSureRate(r.PR?.products),
    classGroup: 'Products',
    shortLabel: 'Correct Sure',
  },
  {
    label: 'Prod Top1 Acc',
    section: 'PR',
    getValue: (r) => prTop1Accuracy(r.PR?.products),
    classGroup: 'Products',
    shortLabel: 'Top1 Acc',
  },
  {
    label: 'Prod Top3 Acc',
    section: 'PR',
    getValue: (r) => prTop3Accuracy(r.PR?.products),
    classGroup: 'Products',
    shortLabel: 'Top3 Acc',
  },
  {
    label: 'Prod Incorr Sure',
    section: 'PR',
    getValue: (r) => prIncorrectSureRate(r.PR?.products),
    lowerIsBetter: true,
    classGroup: 'Products',
    shortLabel: 'Incorr Sure',
  },
  {
    label: 'Prod Incorr Unsure',
    section: 'PR',
    getValue: (r) => prIncorrectUnsureRate(r.PR?.products),
    lowerIsBetter: true,
    classGroup: 'Products',
    shortLabel: 'Incorr Unsure',
  },
  // Products — confidence & thresholds
  {
    label: 'Prod Avg Exp Conf',
    section: 'PR',
    getValue: (r) => r.PR?.products?.avg_exp_confidence,
    fmt: fmtFloat,
    classGroup: 'Products',
    shortLabel: 'Avg Exp Conf',
  },
  {
    label: 'Prod Avg Top1 Conf',
    section: 'PR',
    getValue: (r) => r.PR?.products?.avg_top1_confidence,
    fmt: fmtFloat,
    classGroup: 'Products',
    shortLabel: 'Avg Top1 Conf',
  },
  {
    label: 'Prod Avg Top3 Conf',
    section: 'PR',
    getValue: (r) => r.PR?.products?.avg_top3_confidence,
    fmt: fmtFloat,
    classGroup: 'Products',
    shortLabel: 'Avg Top3 Conf',
  },
  {
    label: 'Prod Rec Sim Thr',
    section: 'PR',
    getValue: (r) => r.PR?.products?.recommended_similarity_thr,
    fmt: fmtFloat,
    classGroup: 'Products',
    shortLabel: 'Rec Sim Thr',
  },
  {
    label: 'Prod Rec Delta Thr',
    section: 'PR',
    getValue: (r) => r.PR?.products?.recommended_delta_thr,
    fmt: fmtFloat,
    classGroup: 'Products',
    shortLabel: 'Rec Delta Thr',
  },
  {
    label: 'Prod Objects',
    section: 'PR',
    getValue: (r) => r.PR?.products?.total_objects,
    fmt: fmtCount,
    classGroup: 'Products',
    shortLabel: 'Objects',
  },
  // Cartons — rates
  {
    label: 'Cart Correct Sure',
    section: 'PR',
    getValue: (r) => prCorrectSureRate(r.PR?.cartons),
    classGroup: 'Cartons',
    shortLabel: 'Correct Sure',
  },
  {
    label: 'Cart Top1 Acc',
    section: 'PR',
    getValue: (r) => prTop1Accuracy(r.PR?.cartons),
    classGroup: 'Cartons',
    shortLabel: 'Top1 Acc',
  },
  {
    label: 'Cart Top3 Acc',
    section: 'PR',
    getValue: (r) => prTop3Accuracy(r.PR?.cartons),
    classGroup: 'Cartons',
    shortLabel: 'Top3 Acc',
  },
  {
    label: 'Cart Incorr Sure',
    section: 'PR',
    getValue: (r) => prIncorrectSureRate(r.PR?.cartons),
    lowerIsBetter: true,
    classGroup: 'Cartons',
    shortLabel: 'Incorr Sure',
  },
  {
    label: 'Cart Incorr Unsure',
    section: 'PR',
    getValue: (r) => prIncorrectUnsureRate(r.PR?.cartons),
    lowerIsBetter: true,
    classGroup: 'Cartons',
    shortLabel: 'Incorr Unsure',
  },
  // Cartons — confidence & thresholds
  {
    label: 'Cart Avg Exp Conf',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.avg_exp_confidence,
    fmt: fmtFloat,
    classGroup: 'Cartons',
    shortLabel: 'Avg Exp Conf',
  },
  {
    label: 'Cart Avg Top1 Conf',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.avg_top1_confidence,
    fmt: fmtFloat,
    classGroup: 'Cartons',
    shortLabel: 'Avg Top1 Conf',
  },
  {
    label: 'Cart Avg Top3 Conf',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.avg_top3_confidence,
    fmt: fmtFloat,
    classGroup: 'Cartons',
    shortLabel: 'Avg Top3 Conf',
  },
  {
    label: 'Cart Rec Sim Thr',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.recommended_similarity_thr,
    fmt: fmtFloat,
    classGroup: 'Cartons',
    shortLabel: 'Rec Sim Thr',
  },
  {
    label: 'Cart Rec Delta Thr',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.recommended_delta_thr,
    fmt: fmtFloat,
    classGroup: 'Cartons',
    shortLabel: 'Rec Delta Thr',
  },
  {
    label: 'Cart Objects',
    section: 'PR',
    getValue: (r) => r.PR?.cartons?.total_objects,
    fmt: fmtCount,
    classGroup: 'Cartons',
    shortLabel: 'Objects',
  },
];

export const PC_POG_METRICS: MetricSpec[] = [
  { label: 'POG Compliant F1', section: 'PC', getValue: (r) => r.PC?.pog?.compliant?.f1 },
  { label: 'POG Non-Compliant F1', section: 'PC', getValue: (r) => r.PC?.pog?.non_compliant?.f1 },
  { label: 'POG Out F1', section: 'PC', getValue: (r) => r.PC?.pog?.out?.f1 },
  { label: 'POG Faceover F1', section: 'PC', getValue: (r) => r.PC?.pog?.faceover?.f1 },
];

export const PC_ROG_METRICS: MetricSpec[] = [
  { label: 'ROG Status/Pos F1', section: 'PC', getValue: (r) => r.PC?.rog?.status_by_position?.f1 },
  { label: 'ROG Item/Pos F1', section: 'PC', getValue: (r) => r.PC?.rog?.item_number_by_position?.f1 },
  { label: 'ROG Item/Img F1', section: 'PC', getValue: (r) => r.PC?.rog?.item_number_by_image?.f1 },
];

export const PC_METRICS: MetricSpec[] = [...PC_POG_METRICS, ...PC_ROG_METRICS];
export const ALL_METRICS: MetricSpec[] = [...OD_METRICS, ...PR_METRICS, ...PC_METRICS];
